import React, { useEffect, useState } from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import { KTIcon } from "../../../../_metronic/helpers";
import { useLocation, useNavigate } from "react-router-dom";
import { get_user_schedule, save_user_schedule } from "../api/usersApi";
import { toast } from "react-toastify";

const step = Number(process.env.REACT_APP_WEEKLY_HOURS_DIFFERENCE) || 15;
interface TimeSlot {
    from: string;
    to: string;
}

interface DayAvailability {
    day: string;
    enabled: boolean;
    slots: TimeSlot[];
}

const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

// Generate 15-minute interval times (12-hour format)
const generateTimeOptions = (): string[] => {
    const times: string[] = [];
    for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += step) {
            const hour = h % 12 === 0 ? 12 : h % 12;
            const suffix = h < 12 ? "AM" : "PM";
            const minute = m.toString().padStart(2, "0");
            times.push(`${hour}:${minute} ${suffix}`);
        }
    }
    return times;
};

const timeOptions = generateTimeOptions();

const WeeklyAvailability: React.FC = () => {
    const location = useLocation();
    const { user } = (location.state as { user: any }) || {};
    const userId = user?.id;
    const navigate = useNavigate();

    const [availability, setAvailability] = useState<DayAvailability[]>(
        daysOfWeek.map((day) => ({
            day,
            enabled: false,
            slots: [{ from: "9:00 AM", to: "5:00 PM" }],
        }))
    );

    const [loading, setLoading] = useState(false);

    // 🔹 Fetch user schedule
    useEffect(() => {
        if (!userId) return;
        const fetchSchedule = async () => {
            try {
                setLoading(true);
                const res = await get_user_schedule(userId);
                if (res?.data?.schedule) {
                    const apiData = res.data.schedule;
                    const updatedAvailability = daysOfWeek.map((day) => {
                        const key = day.toLowerCase();
                        const data = apiData[key];
                        if (data) {
                            return {
                                day,
                                enabled: data.enabled,
                                slots: data.slots
                                    ? [{ from: data.slots.from, to: data.slots.to }]
                                    : [{ from: "9:00 AM", to: "5:00 PM" }],
                            };
                        }
                        return { day, enabled: false, slots: [{ from: "9:00 AM", to: "5:00 PM" }] };
                    });
                    setAvailability(updatedAvailability);
                }
            } catch (err) {
                console.error("Error fetching schedule:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchSchedule();
    }, [userId]);

    // 🔹 Handle toggle
    const handleToggleDay = (index: number) => {
        const updated = [...availability];
        updated[index].enabled = !updated[index].enabled;
        setAvailability(updated);
    };

    // 🔹 Handle time change
    const handleTimeChange = (
        dayIndex: number,
        slotIndex: number,
        field: keyof TimeSlot,
        value: string
    ) => {
        const updated = [...availability];
        updated[dayIndex].slots[slotIndex][field] = value;
        setAvailability(updated);
    };

    // 🔹 Save formatted payload
    const handleSave = async () => {
        try {
            const payload: any = {
                user_id: userId,
            };

            availability.forEach((day) => {
                const key = day.day.toLowerCase();
                if (day.enabled) {
                    payload[key] = {
                        enabled: true,
                        slots: {
                            from: day.slots[0].from,
                            to: day.slots[0].to,
                        },
                    };
                } else {
                    payload[key] = {
                        enabled: false,
                        slots: "",
                    };
                }
            });

            console.log("Final Payload:", payload);
            setLoading(true);
            const res = await save_user_schedule(payload);
            toast.success(res?.message || "Weekly schedule updated successfully!");
            navigate("/users/users-list");
        } catch (error) {
            console.error("Error saving schedule:", error);
            toast.error("Failed to save weekly schedule!");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mt-4 p-3 border rounded bg-white shadow-sm">
            <h4 className="mb-4 fw-semibold text-gray-700">
                Weekly Availability{" "}
                {user && (
                    <span className="text-muted fs-6">
                        – {user.fullname}
                    </span>
                )}
            </h4>

            {availability.map((dayItem, dayIndex) => (
                <div
                    key={dayItem.day}
                    className={`border rounded p-3 mb-3 ${dayItem.enabled ? "bg-light" : "bg-light-subtle opacity-75"
                        }`}
                >
                    <Row className="align-items-center mb-2">
                        <Col xs="auto">
                            <Form.Check
                                type="checkbox"
                                label={dayItem.day}
                                checked={dayItem.enabled}
                                onChange={() => handleToggleDay(dayIndex)}
                            />
                        </Col>
                    </Row>

                    {dayItem.enabled &&
                        dayItem.slots.map((slot, slotIndex) => (
                            <Row key={slotIndex} className="align-items-center mb-2 ms-4">
                                <Col md={3}>
                                    <Form.Select
                                        value={slot.from}
                                        onChange={(e) =>
                                            handleTimeChange(dayIndex, slotIndex, "from", e.target.value)
                                        }
                                    >
                                        {timeOptions.map((time) => (
                                            <option key={time}>{time}</option>
                                        ))}
                                    </Form.Select>
                                </Col>

                                <Col md={3}>
                                    <Form.Select
                                        value={slot.to}
                                        onChange={(e) =>
                                            handleTimeChange(dayIndex, slotIndex, "to", e.target.value)
                                        }
                                    >
                                        {timeOptions.map((time) => (
                                            <option key={time}>{time}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                            </Row>
                        ))}
                </div>
            ))}

            <div className="d-flex justify-content-end mt-4">
                <Button
                    variant="light"
                    className="border me-2 px-4"
                    onClick={() => navigate("/users/users-list")}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    className="px-4"
                    disabled={loading}
                >
                    {loading ? "Saving..." : "Save Availability"}
                </Button>
            </div>
        </div>
    );
};

export default WeeklyAvailability;
